import { useQuery } from '@tanstack/react-query';
import { getCachedArticle, articleToLinkPreview } from '@/lib/articleCache';

export interface VideoEmbed {
  type: 'video';
  embedUrl: string;
  provider: string;
  videoId: string;
  aspectRatio?: string;
}

export interface LinkPreview {
  title?: string;
  description?: string;
  image?: string;
  favicon?: string;
  siteName?: string;
  url: string;
  embed?: VideoEmbed;
}

/**
 * Extract video information from popular video sharing sites
 */
function extractVideoEmbed(url: string): VideoEmbed | null {
  const urlObj = new URL(url);

  // YouTube
  if (urlObj.hostname === 'www.youtube.com' || urlObj.hostname === 'youtube.com') {
    const videoId = urlObj.searchParams.get('v');
    if (videoId) {
      return {
        type: 'video',
        embedUrl: `https://www.youtube.com/embed/${videoId}`,
        provider: 'YouTube',
        videoId,
        aspectRatio: '16/9',
      };
    }
  }

  // YouTube Shorts
  if (urlObj.hostname === 'youtu.be') {
    const videoId = urlObj.pathname.slice(1);
    if (videoId) {
      return {
        type: 'video',
        embedUrl: `https://www.youtube.com/embed/${videoId}`,
        provider: 'YouTube',
        videoId,
        aspectRatio: '16/9',
      };
    }
  }

  // Vimeo
  if (urlObj.hostname === 'vimeo.com' || urlObj.hostname === 'www.vimeo.com') {
    const videoId = urlObj.pathname.split('/')[1];
    if (videoId && /^\d+$/.test(videoId)) {
      return {
        type: 'video',
        embedUrl: `https://player.vimeo.com/video/${videoId}`,
        provider: 'Vimeo',
        videoId,
        aspectRatio: '16/9',
      };
    }
  }

  // Twitch
  if (urlObj.hostname === 'www.twitch.tv' || urlObj.hostname === 'twitch.tv') {
    const pathParts = urlObj.pathname.split('/');
    if (pathParts[1] === 'videos' && pathParts[2]) {
      const videoId = pathParts[2];
      return {
        type: 'video',
        embedUrl: `https://player.twitch.tv/?video=${videoId}&parent=${window.location.hostname}`,
        provider: 'Twitch',
        videoId,
        aspectRatio: '16/9',
      };
    }
  }

  // TikTok
  if (urlObj.hostname === 'www.tiktok.com' || urlObj.hostname === 'tiktok.com') {
    const videoMatch = urlObj.pathname.match(/\/video\/(\d+)/);
    if (videoMatch) {
      const videoId = videoMatch[1];
      return {
        type: 'video',
        embedUrl: `https://www.tiktok.com/embed/v2/${videoId}`,
        provider: 'TikTok',
        videoId,
        aspectRatio: '9/16', // TikTok is vertical
      };
    }
  }

  // Dailymotion
  if (urlObj.hostname === 'www.dailymotion.com' || urlObj.hostname === 'dailymotion.com') {
    const videoMatch = urlObj.pathname.match(/\/video\/([a-zA-Z0-9]+)/);
    if (videoMatch) {
      const videoId = videoMatch[1];
      return {
        type: 'video',
        embedUrl: `https://www.dailymotion.com/embed/video/${videoId}`,
        provider: 'Dailymotion',
        videoId,
        aspectRatio: '16/9',
      };
    }
  }

  return null;
}

/**
 * Hook to fetch link preview data using multiple API services as fallbacks
 * Tries microlink.io first, then falls back to other services if needed
 * Also checks for video embeds from popular video sharing sites
 */
export function useLinkPreview(url: string | URL) {
  url = new URL(url);

  // Check for video embeds first
  const videoEmbed = extractVideoEmbed(url.toString());

  return useQuery({
    queryKey: ['link-preview', url.toString()],
    queryFn: async (context): Promise<LinkPreview> => {
      // First, check if we have this article cached in IndexedDB
      const cachedArticle = await getCachedArticle(url.toString());
      if (cachedArticle) {
        const linkPreview = articleToLinkPreview(cachedArticle);
        // Add video embed if detected
        if (videoEmbed) {
          linkPreview.embed = videoEmbed;
        }
        return linkPreview;
      }

      // Try microlink.io first
      try {
        const microlinkUrl = `https://api.microlink.io/?url=${encodeURIComponent(url.toString())}&screenshot=false&video=false&audio=false`;

        const response = await fetch(microlinkUrl, {
          signal: AbortSignal.any([context.signal, AbortSignal.timeout(1500)]),
        });

        if (response.ok) {
          const result = await response.json();

          if (result.status === 'success' && result.data) {
            const data = result.data;
            return {
              title: data.title || undefined,
              description: data.description || undefined,
              image: data.image?.url || undefined,
              favicon: data.logo?.url || undefined,
              siteName: data.publisher || undefined,
              url: url.toString(),
              embed: videoEmbed || undefined,
            };
          }
        }
      } catch (error) {
        console.warn('Microlink.io failed, trying fallback:', error);
      }

      // Fallback to linkpreview.net
      try {
        const linkPreviewUrl = `https://api.linkpreview.net/?key=demo&q=${encodeURIComponent(url.toString())}`;

        const response = await fetch(linkPreviewUrl, {
          signal: AbortSignal.any([context.signal, AbortSignal.timeout(1500)]),
        });

        if (response.ok) {
          const data = await response.json();

          if (data.title || data.description) {
            return {
              title: data.title || undefined,
              description: data.description || undefined,
              image: data.image || undefined,
              favicon: data.url ? `https://external-content.duckduckgo.com/ip3/${new URL(data.url).hostname}.ico` : undefined,
              siteName: data.sitename || undefined,
              url: url.toString(),
              embed: videoEmbed || undefined,
            };
          }
        }
      } catch (error) {
        console.warn('LinkPreview.net failed:', error);
      }

      // If all else fails, throw an error
      throw new Error(`Failed to fetch link preview for ${url.toString()}`);
    },
    placeholderData: {
      title: url.toString(),
      description: videoEmbed ? `Watch on ${videoEmbed.provider}` : `Visit ${url.hostname}`,
      favicon: `https://external-content.duckduckgo.com/ip3/${url.hostname}.ico`,
      url: url.toString(),
      embed: videoEmbed || undefined,
    },
    enabled: !!url,
    staleTime: 1000 * 60 * 60, // 1 hour
    gcTime: 1000 * 60 * 60 * 24, // 24 hours
    retry: 2,
    retryDelay: 1000,
  });
}